import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { CHECKOUT_PAGE_OPTION, KeepDateTimeEnum, PricingPlan, SETTING_TYPE } from '@/constants/enum';
import { IMAGE } from '@/helpers/link';
import { StyledPadding } from '@/pages/GeneralSetting/styled';
import { generalSettingDisableSelector, generalSettingSelector, handleSaveSettings } from '@/redux/slice/setting.slice';
import { BlockStack, Box, Card, Checkbox, Divider, Icon, InlineStack, Link, Tabs } from '@shopify/polaris';
import { PageReferenceIcon } from '@shopify/polaris-icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ViewDemoModal } from './ViewDemoModal';
import { PlanMessage } from '@/components/PlanMessage';

type CheckoutPageOption = {
  id: string;
  title: string;
  helpText: string;
};

type CheckoutPageOptionsData = {
  shipping: CheckoutPageOption[];
  delivery: CheckoutPageOption[];
  pickup: CheckoutPageOption[];
};

export const CheckoutPage = () => {
  const { t } = useTranslation('general_settings');
  const generalSettings = useSelector(generalSettingSelector);
  const dispatch = useDispatch();
  const [modal, setModal] = useState({
    isOpenViewDemoProduct: false,
    isOpenViewDemoCart: false,
  });

  const DEFAULT_CHECKOUTPAGE_KEEP_DATE_TIME = useMemo(
    () => [KeepDateTimeEnum.SHIPPING_NAME, KeepDateTimeEnum.DELIVERY_NAME, KeepDateTimeEnum.PICKUP_NAME],
    [],
  );

  const getUpdatedKeepDateTime = useCallback(
    (currentTags: string[], tag: string) => {
      const hasTag = currentTags.includes(tag);
      const newTags = hasTag ? currentTags.filter((t) => t !== tag) : [...currentTags, tag];

      return DEFAULT_CHECKOUTPAGE_KEEP_DATE_TIME.filter((t) => newTags.includes(t));
    },
    [DEFAULT_CHECKOUTPAGE_KEEP_DATE_TIME],
  );

  const checkoutPageMethod = useMemo(
    () => [
      {
        id: SETTING_TYPE.SHIPPING,
        content: t('sections.advancedSetting.checkoutPage.method.0'),
        panelID: 'checkout-page-shipping',
      },
      {
        id: SETTING_TYPE.DELIVERY,
        content: t('sections.advancedSetting.checkoutPage.method.1'),
        panelID: 'checkout-page-delivery',
      },
      {
        id: SETTING_TYPE.PICKUP,
        content: t('sections.advancedSetting.checkoutPage.method.2'),
        panelID: 'checkout-page-pickup',
      },
    ],
    [t],
  );

  const checkoutOptionsData = useMemo(
    () => ({
      shipping: [
        {
          id: '0',
          title: t('sections.advancedSetting.checkoutPage.methodContent.shipping.0.title'),
          helpText: t('sections.advancedSetting.checkoutPage.methodContent.shipping.0.helpText'),
          keepMethod: KeepDateTimeEnum.SHIPPING_NAME,
        },
      ],
      delivery: [
        {
          id: '0',
          title: t('sections.advancedSetting.checkoutPage.methodContent.delivery.0.title'),
          helpText: t('sections.advancedSetting.checkoutPage.methodContent.delivery.0.helpText'),
          keepMethod: KeepDateTimeEnum.DELIVERY_NAME,
        },
      ],
      pickup: [
        {
          id: '0',
          title: t('sections.advancedSetting.checkoutPage.methodContent.pickup.0.title'),
          helpText: t('sections.advancedSetting.checkoutPage.methodContent.pickup.0.helpText'),
          keepMethod: KeepDateTimeEnum.PICKUP_NAME,
        },
      ],
    }),
    [t],
  );

  const [selectTabMethod, setSelectTabMethod] = useState(checkoutPageMethod[0].id);

  const selectedMethodOptions = checkoutOptionsData[selectTabMethod as keyof CheckoutPageOptionsData];
  const generalSettingDisable = useSelector(generalSettingDisableSelector);

  return (
    <Card>
      <BlockStack gap={'300'}>
        <Box>
          <InlineStack blockAlign="center" align="space-between">
            <InlineStack gap={'200'}>
              <Box>
                <Icon source={PageReferenceIcon} />
              </Box>
              <BoldText>{t('sections.advancedSetting.checkoutPage.title')}</BoldText>
            </InlineStack>
            {generalSettingDisable.isDisableCheckoutPageSetting ? (
              <PlanMessage plan={PricingPlan.Smart} />
            ) : (
              <Link
                removeUnderline
                onClick={() =>
                  window.open(
                    'https://help.omegatheme.com/en/article/how-to-display-selected-date-and-time-on-checkout-page-1xuuynd/?bust=1750820667663',
                  )
                }
              >
                {t('sections.advancedSetting.checkoutPage.viewDemo')}
              </Link>
            )}
          </InlineStack>
          <StyledPadding paddingLeft="28px">
            <Box color="text-secondary">{t('sections.advancedSetting.checkoutPage.helpText')}</Box>
          </StyledPadding>
        </Box>
        <Card>
          <BlockStack gap={'100'}>
            <InlineStack blockAlign="center" align="space-between" gap={'150'}>
              <RegularText fontWeight="medium">{t('sections.advancedSetting.checkoutPage.checkoutLabel')}</RegularText>
              <Switch
                isActive={generalSettings.isChangeMethodShipping !== CHECKOUT_PAGE_OPTION.OFF}
                onSwitch={() =>
                  dispatch(
                    handleSaveSettings({
                      isChangeMethodShipping:
                        generalSettings.isChangeMethodShipping !== CHECKOUT_PAGE_OPTION.OFF
                          ? CHECKOUT_PAGE_OPTION.OFF
                          : CHECKOUT_PAGE_OPTION.ON,
                    }),
                  )
                }
                isDisabled={generalSettingDisable.isDisableCheckoutPageSetting}
              />
            </InlineStack>
            <RegularText tone="subdued">{t('sections.advancedSetting.checkoutPage.checkoutHelpText')}</RegularText>
          </BlockStack>
          {generalSettings.isChangeMethodShipping === CHECKOUT_PAGE_OPTION.ON && (
            <>
              <Box paddingBlockStart={'500'} paddingBlockEnd={'200'}>
                <Divider />
              </Box>
              <Tabs
                tabs={checkoutPageMethod}
                selected={checkoutPageMethod.findIndex((tab) => tab.id === selectTabMethod)}
                onSelect={(index) => setSelectTabMethod(checkoutPageMethod[index].id)}
                fitted
              />
              {selectedMethodOptions.map((option) => (
                <Checkbox
                  key={option.id}
                  label={option.title}
                  helpText={option.helpText}
                  checked={generalSettings.keepDateTime.includes(option.keepMethod)}
                  onChange={() =>
                    dispatch(
                      handleSaveSettings({
                        keepDateTime: getUpdatedKeepDateTime(generalSettings.keepDateTime, option.keepMethod),
                      }),
                    )
                  }
                  disabled={generalSettingDisable.isDisableCheckoutPageSetting}
                />
              ))}
            </>
          )}
        </Card>
      </BlockStack>
      <ViewDemoModal
        open={modal.isOpenViewDemoProduct}
        onClose={() => setModal({ ...modal, isOpenViewDemoProduct: false })}
        imgSrc={IMAGE.DEMO_PRODUCT_PAGE_IMG}
        size="large"
      />
      <ViewDemoModal
        open={modal.isOpenViewDemoCart}
        onClose={() => setModal({ ...modal, isOpenViewDemoCart: false })}
        imgSrc={IMAGE.DEMO_CART_PAGE_IMG}
        size="large"
      />
    </Card>
  );
};
